import React from "react";
import Chip from "../../assets/icons/card-nfc.svg";
import Seal from "../../assets/icons/card-badge.svg";
import { StaffRole } from "../../data/staff";
import { hasImpression, impressionSrc } from "../../data/staffImpressions";
import "./StaffCard.scss";

const ROLE_LABEL: Record<StaffRole, string> = {
  Professor: "Professor",
  "Head TA": "HTA",
  STA: "STA",
  UTA: "UTA",
};

export interface StaffCardProps {
  name: string;
  cslogin: string;
  pronouns: string;
  blurb: string;
  image: string;
  altImage: string;
  role: StaffRole;
}

interface StaffCardState {
  playing: boolean;
}

export class StaffCard extends React.Component<StaffCardProps, StaffCardState> {
  private audio: HTMLAudioElement | null = null;
  private photoBack: HTMLImageElement | null = null;
  state: StaffCardState = { playing: false };

  componentWillUnmount() {
    this.audio?.pause();
    this.audio = null;
  }

  restartAltIfGif = () => {
    const img = this.photoBack;
    if (!img?.src.includes(".gif")) {
      return;
    }

    const { src } = img;
    img.src = "";
    img.src = src;
  };

  bindAudio = () => {
    if (this.audio) {
      return this.audio;
    }

    const audio = new Audio(impressionSrc(this.props.cslogin));
    audio.addEventListener("ended", () => this.setState({ playing: false }));
    this.audio = audio;
    return audio;
  };

  playImpression = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const audio = this.bindAudio();
    audio.pause();
    audio.currentTime = 0;
    this.setState({ playing: true });
    void audio.play().catch(() => this.setState({ playing: false }));
  };

  render() {
    const { name, cslogin, pronouns, blurb, image, altImage, role } = this.props;
    const { playing } = this.state;
    const hasAlt = Boolean(altImage) && altImage !== image;
    const gifAlt = hasAlt && altImage.includes(".gif");

    return (
      <article className={`staff-card${hasAlt ? " has-alt" : ""}`}>
        <div className="staff-card-left">
          <p className="staff-card-league">CS0150 League</p>
          <div className="staff-card-photo" onMouseEnter={this.restartAltIfGif}>
            <img className="staff-card-photo-front" src={image} alt="" />
            {hasAlt ? (
              <img
                className={`staff-card-photo-back${gifAlt ? " is-gif" : ""}`}
                src={altImage}
                alt=""
                ref={(node) => {
                  this.photoBack = node;
                }}
              />
            ) : null}
          </div>
          <div className="staff-card-name-row">
            <h3 className="staff-card-name">{name}</h3>
          </div>
        </div>

        <div className="staff-card-right">
          <div className="staff-card-top">
            <div className="staff-card-department">
              <p className="staff-card-label">Department</p>
              <p className="staff-card-role">{ROLE_LABEL[role]}</p>
            </div>
            <div className="staff-card-corner">
              {hasImpression(role) && (
                <button
                  aria-label={
                    playing
                      ? `${name}'s minion impression playing`
                      : `Play ${name}'s minion impression`
                  }
                  aria-pressed={playing}
                  className={`staff-card-speak${playing ? " is-playing" : ""}`}
                  onClick={this.playImpression}
                  type="button"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      className="play-mark"
                      d="M10.05 8.15c0-.42.46-.68.82-.46l6.05 3.85c.34.22.34.7 0 .92l-6.05 3.85c-.36.22-.82-.04-.82-.46V8.15z"
                    />
                    <g className="eq-bars">
                      <rect className="eq-bar eq-bar-1" x="8.05" y="8.4" width="2.05" height="7.2" rx="1" />
                      <rect className="eq-bar eq-bar-2" x="11" y="6.7" width="2.05" height="10.6" rx="1" />
                      <rect className="eq-bar eq-bar-3" x="13.95" y="7.7" width="2.05" height="8.6" rx="1" />
                    </g>
                  </svg>
                </button>
              )}
              <div className="staff-card-seal" aria-hidden="true">
                <img src={Seal} alt="" />
              </div>
            </div>
          </div>

          <div className="staff-card-meta">
            <img className="staff-card-chip" src={Chip} alt="" />
            <div className="staff-card-meta-copy">
              <p>
                <span>CS Login</span>
                {cslogin}
              </p>
              {pronouns ? (
                <p>
                  <span>Pronouns</span>
                  {pronouns}
                </p>
              ) : null}
            </div>
          </div>

          <p className="staff-card-bio">{blurb}</p>
        </div>
      </article>
    );
  }
}
